import { useRemediationFilter } from "~/components/Remediation/hooks/useRemediationFilter";
import React, { Dispatch, SetStateAction, useEffect, useState } from "react";
import {
  GetRemediationQuery,
  useGetAffectedEcosystemsLazyQuery,
  useGetRemediationLazyQuery,
} from "~/operations";
import { RemediationProps } from "../Remediation";
import {
  isPackageToRemediate,
  PACKAGES_TO_REMEDIATE,
} from "~/components/Remediation/constants";
import { SelectChangeEvent } from "@mui/material";
import { pluralize } from "~/lib/pluralize";
import {
  AffectedEcosystemsType,
  AffectedEcosystemVersionsType,
  PackageToRemediateType,
  RemediationPackagesType,
  RemediationScriptsType,
} from "~/components/Remediation/types";
import { ApolloError } from "@apollo/client";

type UseRemediationReturn = {
  packagesType: PackageToRemediateType;
  setSelectedPackagesType: Dispatch<SetStateAction<PackageToRemediateType>>;
  remediationScripts: RemediationScriptsType;
  remediationPackages: RemediationPackagesType;
  remediationError?: ApolloError | undefined;
  ecosystemTitle: string;
  ecosystemVersion: string;
  selectedEcosystemsVersions: AffectedEcosystemVersionsType;
  showEcosystems: boolean;
  showPackages: boolean;
  fixAllPackagesLabel: string;
  remediationForScopeResponse: GetRemediationQuery["remediationForScope"];
  selectedPackages: string[];
  ecosystems: AffectedEcosystemsType;
  handleEcosystemTitleChange: (event: SelectChangeEvent) => void;
  handleEcosystemVersionChange: (event: SelectChangeEvent) => void;
  handlePackagesChange: (
    _event: React.ChangeEvent<HTMLInputElement>,
    id: string,
  ) => void;
  handlePackagesToIncludeClick: (
    _e: React.ChangeEvent<HTMLInputElement>,
    value: string,
  ) => void;
  isLoading: boolean;
  hideRemediationSection: boolean;
  isRemediationEmpty: boolean;
};

export function useRemediation({
  packages = [],
  findingId,
  scopeMrn,
  isAssetContext,
  initiativeMrn,
}: RemediationProps): UseRemediationReturn {
  const {
    setTitle,
    setVersion,
    setPackages,
    getTitle,
    getVersion,
    applyFilter,
    getPackages,
  } = useRemediationFilter();

  const [packagesType, setSelectedPackagesType] =
    useState<PackageToRemediateType>(
      getPackages().length > 0
        ? PACKAGES_TO_REMEDIATE.INDIVIDUAL
        : PACKAGES_TO_REMEDIATE.ALL,
    );

  const [fetchRemediation, { data, loading: remediationLoading, error }] =
    useGetRemediationLazyQuery();

  const [
    fetchEcosystems,
    { data: ecosystemsData, loading: ecosystemsLoading },
  ] = useGetAffectedEcosystemsLazyQuery();

  const remediationForScopeResponse = data?.remediationForScope;
  const remediation =
    remediationForScopeResponse?.__typename === "Remediation"
      ? remediationForScopeResponse
      : undefined;

  const remediationPackages: RemediationPackagesType =
    remediation?.allPackages || [];
  const remediationScripts: RemediationScriptsType = remediation?.scripts || [];
  const ecosystems: AffectedEcosystemsType =
    ecosystemsData?.affectedEcosystems || [];

  useEffect(() => {
    if (isAssetContext) {
      fetchRemediations();
    }
  }, [isAssetContext]);

  useEffect(() => {
    const hasRequiredId = findingId || initiativeMrn;
    if (!scopeMrn || isAssetContext || !hasRequiredId) return;

    fetchEcosystems({
      variables: {
        vulnId: findingId || "",
        scopeMrn,
        ...(initiativeMrn && { initiativeMrn }),
      },
    }).then(({ data }) => {
      // Setting default dropdown and checkboxes values
      const ecosystems = data?.affectedEcosystems || [];

      const title = ecosystems.at(0)?.friendlyName;
      const version = ecosystems.at(0)?.versions?.at(0)?.ecosystem;

      const titleFromParams = getTitle();
      const versionFromParams = getVersion();

      if (!titleFromParams) {
        setTitle(title || "");
      }
      if (!versionFromParams) {
        setVersion(version || "");
      }
      applyFilter();

      if (!isAssetContext) {
        fetchRemediations();
      }
    });
  }, [scopeMrn, findingId, initiativeMrn]);

  const fetchRemediations = () => {
    if (!scopeMrn) return;

    const ecosystem = getVersion() || getTitle();

    if (!ecosystem && !isAssetContext) return;

    // Get packages from remediation data or fallback to passed packages
    const selectedPackages = getPackages();
    const availablePackages =
      selectedPackages.length > 0
        ? remediationPackages
            .filter((rp) => selectedPackages.includes(rp.id))
            .map((p) => ({ name: p.name }))
        : packages || [];

    return fetchRemediation({
      variables: {
        ...(findingId && { vulnId: findingId }),
        scopeMrn,
        ...(ecosystem && { ecosystem }),
        packages: availablePackages,
        ...(initiativeMrn && { initiativeMrn }),
      },
    });
  };

  const handleEcosystemTitleChange = (event: SelectChangeEvent) => {
    setTitle(event.target.value || "");
    // when selecting ecosystem, preselect it's first available version in dropdown
    const selectedEcosystemId = (
      ecosystems.find((e) => e.friendlyName === event.target.value)?.versions ||
      []
    ).at(0)?.ecosystem;
    setVersion(selectedEcosystemId || "");
    applyFilter();
    fetchRemediations();
  };

  const handleEcosystemVersionChange = (event: SelectChangeEvent) => {
    setVersion(event.target.value || "");
    applyFilter();
    fetchRemediations();
  };

  const handlePackagesChange = (
    _event: React.ChangeEvent<HTMLInputElement>,
    id: string,
  ) => {
    if (!scopeMrn) return;

    const selectedPackages = getPackages();

    const updatedSelection = selectedPackages.includes(id)
      ? selectedPackages.filter((checkboxId) => checkboxId !== id)
      : [...selectedPackages, id];

    setPackages(updatedSelection);
    applyFilter();
    fetchRemediations();
  };

  const handlePackagesToIncludeClick = (
    _e: React.ChangeEvent<HTMLInputElement>,
    value: string,
  ) => {
    setSelectedPackagesType(
      isPackageToRemediate(value) ? value : PACKAGES_TO_REMEDIATE.ALL,
    );

    if (value === PACKAGES_TO_REMEDIATE.ALL) {
      setPackages([]);
    }

    applyFilter();
  };

  const ecosystemTitle = getTitle() || "";
  const ecosystemVersion = getVersion() || "";
  const selectedEcosystemsVersions: AffectedEcosystemVersionsType =
    ecosystems.find((e) => e.friendlyName === ecosystemTitle)?.versions || [];
  const showPackages = remediationPackages.length > 1;
  const fixAllPackagesLabel = `Fix all ${remediationPackages.length} ${pluralize("package", remediationPackages.length)}`;

  const isRemediationEmpty =
    data?.remediationForScope?.__typename !== "Remediation";
  const hasOnlyOneEcosystemOption =
    ecosystems.length === 1 && selectedEcosystemsVersions.length === 1;
  const showEcosystems = !hasOnlyOneEcosystemOption && !isAssetContext;

  return {
    packagesType,
    setSelectedPackagesType,
    remediationScripts,
    remediationError: error,
    ecosystemTitle,
    ecosystemVersion,
    selectedEcosystemsVersions,
    showEcosystems,
    showPackages,
    fixAllPackagesLabel,
    remediationPackages,
    remediationForScopeResponse,
    selectedPackages: getPackages(),
    ecosystems,
    handleEcosystemTitleChange,
    handleEcosystemVersionChange,
    handlePackagesChange,
    handlePackagesToIncludeClick,
    isLoading: remediationLoading || ecosystemsLoading,
    hideRemediationSection: ecosystems.length === 0 && !isAssetContext,
    isRemediationEmpty,
  };
}
